import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, MessagingConversation, Message } from '@nexus/shared/types';
import { toast } from './toastStore';

interface MessagingState {
  conversations: MessagingConversation[];
  messages: Message[];
  selectedId: string | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  fetchConversations: (params?: Record<string, string | number | undefined>) => Promise<void>;
  fetchMessages: (conversationId: string) => Promise<void>;
  send: (data: Record<string, unknown>) => Promise<void>;
  selectConversation: (id: string | null) => void;
  clearError: () => void;
}

function convertConvo(c: Record<string, unknown>): MessagingConversation {
  return {
    ...c,
    createdAt: fromUnix(c.createdAt as number),
    lastMessageAt: c.lastMessageAt ? fromUnix(c.lastMessageAt as number) : undefined,
  } as MessagingConversation;
}

function convertMsg(m: Record<string, unknown>): Message {
  return { ...m, createdAt: fromUnix(m.createdAt as number) } as Message;
}

export const useMessagingStore = create<MessagingState>((set, get) => ({
  conversations: [],
  messages: [],
  selectedId: null,
  total: 0,
  isLoading: false,
  error: null,

  fetchConversations: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<MessagingConversation[]>>(`/api/messaging/conversations${toQueryString(params)}`);
      const conversations = (res.data ?? []).map(c => convertConvo(c as unknown as Record<string, unknown>));
      set({ conversations, total: res.meta?.total ?? 0, isLoading: false });
      if (conversations.length > 0 && !get().selectedId) {
        set({ selectedId: conversations[0].id });
        get().fetchMessages(conversations[0].id);
      }
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchMessages: async (conversationId) => {
    try {
      const res = await api.get<ApiResponse<Message[]>>(`/api/messaging/conversations/${conversationId}/messages`);
      set({ messages: (res.data ?? []).map(m => convertMsg(m as unknown as Record<string, unknown>)) });
    } catch { /* ignore */ }
  },

  send: async (data) => {
    try {
      await api.post('/api/messaging/send', data);
      const convoId = (data as Record<string, string>).conversationId ?? get().selectedId;
      if (convoId) get().fetchMessages(convoId);
      get().fetchConversations();
      toast.success('Message sent');
    } catch (err) {
      toast.error('Failed to send message', (err as Error).message);
      throw err;
    }
  },

  selectConversation: (id) => {
    set({ selectedId: id });
    if (id) get().fetchMessages(id);
  },

  clearError: () => set({ error: null }),
}));
